import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { logger } from '../utils/logger';
import {
  BookOpen,
  Plus,
  Search,
  RefreshCcw,
  Edit3,
  Trash2,
  XCircle,
  Save,
  Tag,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KnowledgeTraining = ({ agent }) => {
  const [snippets, setSnippets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    keywords: '',
    content: '',
    priority: 50,
    is_active: true
  });

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/knowledge/stats`, {
        params: { requester_id: agent?.id }
      });
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const fetchSnippets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (agent?.id) {
        params.append('requester_id', agent.id);
      }

      const response = await axios.get(`${API}/knowledge?${params}`);
      setSnippets(response.data.items || []);
    } catch (error) {
      logger.error('Error fetching snippets:', error);
      setSnippets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setSelectedSnippet(null);
    setFormData({
      title: '',
      keywords: '',
      content: '',
      priority: 50,
      is_active: true
    });
  };

  const handleEdit = (snippet) => {
    setIsCreating(false);
    setSelectedSnippet(snippet);
    setFormData({
      title: snippet.title || '',
      keywords: (snippet.keywords || []).join(', '),
      content: snippet.content || '',
      priority: snippet.priority || 50,
      is_active: snippet.is_active !== false
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Title and Content are required');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
        content: formData.content.trim(),
        priority: parseInt(formData.priority) || 50,
        is_active: formData.is_active,
        requester_id: agent?.id
      };

      if (isCreating) {
        await axios.post(`${API}/knowledge`, payload);
        alert('Knowledge snippet created successfully!');
      } else if (selectedSnippet) {
        await axios.put(`${API}/knowledge/${selectedSnippet.id}`, payload);
        alert('Knowledge snippet updated successfully!');
      }

      setIsCreating(false);
      setSelectedSnippet(null);
      fetchSnippets();
      fetchStats();
    } catch (error) {
      logger.error('Error saving snippet:', error);
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this knowledge snippet?')) return;

    try {
      await axios.delete(`${API}/knowledge/${id}`, {
        params: { requester_id: agent?.id }
      });
      fetchSnippets();
      fetchStats();
      if (selectedSnippet?.id === id) {
        setSelectedSnippet(null);
      }
    } catch (error) {
      logger.error('Error deleting snippet:', error);
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleActive = async (snippet) => {
    try {
      await axios.put(`${API}/knowledge/${snippet.id}`, {
        is_active: !snippet.is_active,
        requester_id: agent?.id
      });
      fetchSnippets();
      fetchStats();
    } catch (error) {
      logger.error('Error toggling status:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSnippets();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSnippets();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-indigo-600" />
            Knowledge Training
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Add custom knowledge snippets to correct or supplement bot responses
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Knowledge
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Snippets</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
              </div>
              <BookOpen className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats?.active || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-600">{stats?.inactive || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Refresh */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search snippets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              onClick={fetchSnippets}
              disabled={loading}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Snippets List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 text-center py-12">
            <RefreshCcw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Loading snippets...</p>
          </div>
        ) : snippets.length === 0 ? (
          <div className="col-span-2 text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No knowledge snippets yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Click "Add Knowledge" to create your first snippet
            </p>
          </div>
        ) : (
          snippets.map((snippet) => (
            <Card
              key={snippet.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                snippet.is_active ? 'border-green-200' : 'border-gray-200 opacity-60'
              } ${selectedSnippet?.id === snippet.id ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => handleEdit(snippet)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    {snippet.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Badge className={snippet.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {snippet.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge className="bg-indigo-100 text-indigo-800">
                      P{snippet.priority}
                    </Badge>
                  </div>
                </div>

                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {snippet.content}
                </p>

                <div className="flex flex-wrap gap-1 mb-3">
                  {(snippet.keywords || []).slice(0, 5).map((keyword, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      <Tag className="w-3 h-3" />
                      {keyword}
                    </span>
                  ))}
                  {(snippet.keywords || []).length > 5 && (
                    <span className="text-xs text-gray-400">
                      +{snippet.keywords.length - 5} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Created: {formatDate(snippet.created_at)}</span>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggleActive(snippet)}
                      className={`p-1 rounded hover:bg-gray-100 ${
                        snippet.is_active ? 'text-green-600' : 'text-gray-400'
                      }`}
                      title={snippet.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(snippet.id)}
                      className="p-1 rounded text-red-500 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {(isCreating || selectedSnippet) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                {isCreating ? 'Add Knowledge Snippet' : 'Edit Knowledge Snippet'}
              </h3>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setSelectedSnippet(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title / Topic *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Contact Information, Pricing Details"
                />
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keywords (comma-separated)
                </label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="e.g., contact, email, phone, reach us"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These keywords trigger when this snippet should be used
                </p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content * (supports markdown)
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter the correct information that the bot should use..."
                  className="w-full h-48 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                />
              </div>

              {/* Priority and Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (1-100)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Higher priority = more likely to be used
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Help text */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <h4 className="text-sm font-semibold text-indigo-800 mb-2">How it works</h4>
                <ul className="text-xs text-indigo-700 space-y-1">
                  <li>• When a user asks a question containing any of the keywords, this snippet will be included in the bot's context</li>
                  <li>• The bot will use this verified information with highest priority</li>
                  <li>• Great for correcting specific facts like contact details, pricing, or company info</li>
                  <li>• Multiple snippets can be used together for a single query</li>
                </ul>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setSelectedSnippet(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
              >
                {isSaving ? (
                  <RefreshCcw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeTraining;
